import { Button, Checkbox, FormControlLabel, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from '@material-ui/core';
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { trackPromise } from 'react-promise-tracker';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import axios from '../../../axios/AxiosInterceptors';
import { onError, onSuccess } from '../../../store/actions/popupActions';
import useStyles from '../../../Styles';
import PaginatedTableActions from '../../Table/PaginatedTableActions';

const PeakManagementDistributionList = (props) => {

    const classes = useStyles(props);
    const params = useParams();
    const dispatch = useDispatch();
    const [recipients, setRecipients] = useState([]);
    const [deletedRecipients, setDeletedRecipients] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState("name");
    const [direction, setDirection] = useState("asc");
    const [peakPeriods, setPeakPeriods] = useState([{ iso: 'PJM', zone: '' }])

    useEffect(() => {
        retrievePeakPeriods();
        retrieveDistrbutionList(0, pageSize, orderBy, direction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const retrievePeakPeriods = async () => {
        await trackPromise(axios.get("/peak-manager/v1/peak/period").then(response => {
            if (response.data) {
                let periods = [...response.data];
                periods.sort(function (c1, c2) {
                    let c1Zone = c1.zone !== null ? c1.zone : '';
                    let c2Zone = c2.zone !== null ? c2.zone : '';
                    if (c1Zone < c2Zone) { return -1; }
                    if (c1Zone > c2Zone) { return 1; }
                    return 0;
                })
                setPeakPeriods([...periods]);
            }
        }).catch(error => {
            dispatch(onError(error.response));
        }))
    }

    const retrieveDistrbutionList = async (pageNumber, pageSize, orderBy, direction) => {
        let urlParams = { pageSize: pageSize, pageNumber: pageNumber, orderBy: orderBy, direction: direction };
        await trackPromise(axios.get("/peak-manager/v1/account/" + params.accountId + "/distributionList", { params: { ...urlParams } }).then(response => {
            if (response.data && response.data.results && response.data.results.length > 0) {
                setRecipients({ ...response.data });
            } else {
                setRecipients({ pageSize: 1, pageNumber: 0, totalCount: 1, results: [{ name: '', emailAddress: '', phoneNumber: '', iso: '', zone: '', emailEnabled: false, textEnabled: false, disabled: false, sendAction: true }] });
            }
        })).catch(error => {
            dispatch(onError(error));
        })
    }

    const exportToCsv = async () => {
        await trackPromise(axios.get("/peak-manager/v1/account/" + params.accountId + "/distributionList", { headers: { 'Accept': 'text/csv' } }).then(response => {
            let filenameHeader = response.headers['content-disposition'];
            const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content.type'] }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filenameHeader.substring(filenameHeader.indexOf('=') + 2, filenameHeader.lastIndexOf('"')));
            document.body.appendChild(link);
            link.click();
        }
        )).catch(error => {
            dispatch(onError(error));
        })
    }

    const saveDistributionList = async () => {
        let distributionList = recipients.results;
        for (let dl of distributionList) {
            dl.sendAction = true
        };
        distributionList = distributionList.filter(r => r.name !== '');
        if (deletedRecipients && deletedRecipients.length > 0) {
            for (let deletedRecipient of deletedRecipients) {
                await trackPromise(axios.delete("/peak-manager/v1/account/" + params.accountId + "/distributionList/" + deletedRecipient.id)).catch(error => {
                    dispatch(onError(error));
                })
            }
        }
        await trackPromise(axios.post("/peak-manager/v1/account/" + params.accountId + "/distributionList", JSON.stringify(distributionList)).then(response => {
            if (response.data) {
                setRecipients({ ...response.data });
            }
            dispatch(onSuccess("Saved distribution list"));
        })).catch(error => {
            dispatch(onError(error));
        })
    }

    const handleValueChange = (e, index, fieldName) => {
        let recipient = recipients.results[index];
        if (recipient) {
            recipient[fieldName] = e.target.value;
            recipients.results.splice(index, 1, recipient);
            setRecipients({ ...recipients });
        }
    }

    const handlePhoneNumberValueChange = (value, index) => {
        let recipient = recipients.results[index];
        if (recipient) {
            recipient.phoneNumber = value.value;
            recipients.results.splice(index, 1, recipient);
            setRecipients({ ...recipients });
        }
    }

    const handleIsoValueChange = (e, value, index, fieldName) => {
        let recipient = recipients.results[index];
        if (recipient) {
            if (value) {
                recipient[fieldName] = value;
            } else {
                recipient[fieldName] = '';
            }
            if (fieldName === 'iso' && value !== 'PJM') {
                recipient.zone = '';
            }
            recipients.results.splice(index, 1, recipient);
            setRecipients({ ...recipients });
        }
    }

    const handleZoneValueChange = (e, value, index, fieldName) => {
        let recipient = recipients.results[index];
        if (recipient) {
            if (value) {
                recipient[fieldName] = value.zone;
            } else {
                recipient[fieldName] = '';
            }
            if (fieldName === 'iso' && value !== 'PJM') {
                recipient.zone = undefined;
            }
            recipients.results.splice(index, 1, recipient);
            setRecipients({ ...recipients });
        }
    }

    const handleCheckBoxChange = (e, value, index, fieldName) => {
        let recipient = recipients.results[index];
        if (recipient) {
            recipient[fieldName] = !recipient[fieldName];
            recipients.results.splice(index, 1, recipient);
            setRecipients({ ...recipients });
        }
    }

    const renderAddIcon = (totalCount, index) => {
        if (index === totalCount) {
            return (
                <IconButton onClick={addIconClick}>
                    <AddIcon />
                </IconButton>
            )
        }
    }

    const addIconClick = () => {
        recipients.results.push({ name: '', emailAddress: '', phoneNumber: '', iso: '', zone: '', emailEnabled: false, textEnabled: false, disabled: false });
        setRecipients({ ...recipients });
    }

    const removeIconClick = (index) => {
        deletedRecipients.push(recipients.results[index]);
        recipients.results.splice(index, 1);
        setDeletedRecipients([...deletedRecipients]);
        setRecipients({ ...recipients });
    }

    const onSave = () => {
        saveDistributionList();
    }

    const handleSort = (column) => {
        let sortDirection = column === orderBy && direction === 'asc' ? 'desc' : 'asc'
        setDirection(sortDirection);
        setOrderBy(column);
        retrieveDistrbutionList(0, pageSize, column, sortDirection);
    }

    const handlePageChange = (pageNumber, pageSize) => {
        retrieveDistrbutionList(pageNumber, pageSize, orderBy, direction);
    }

    const calculateRowsPerPage = (rowsPerPage) => {
        if (!rowsPerPage) {
            return 10;
        }
        if (rowsPerPage <= 10) {
            return 10;
        }
        if (rowsPerPage <= 25) {
            return 25;
        }
        if (rowsPerPage <= 50) {
            return 50;
        }
        return rowsPerPage;
    }

    const handleRowsPerPageChange = (e) => {
        setPageSize(e.target.value);
        retrieveDistrbutionList(0, e.target.value, orderBy, direction);
    }

    const getOptionSelected = (option, value) => {
        if (option && option.zone && value && value.zone) {
            return option.zone === value.zone;
        }
        if (option && option.zone && value && !value.zone) {
            return option.zone === value;
        }
        if (option && !option.zone && value && value.zone) {
            return option === value.zone;
        }
        if (option && !option.zone && value && !value.zone) {
            return true;
        }
        if (option && (!option.zone || option.zone === null) && value && !value.zone && value === '') {
            return true;
        }
        return option.zone === value;
    }

    const getOptionLabel = (option) => {
        if (option) {
            if (option.zone) {
                return option.zone;
            }
            if (option.zone === null) {
                return "";
            }
        }
        if (option && !option.zone) {
            return option;
        }
        return '';
    }

    const renderRecipients = () => {
        const rows = [];
        if (recipients && recipients.results && recipients.results.length > 0) {
            for (let i = 0; i < recipients.results.length; i++) {
                rows.push(
                    <React.Fragment key={i}>
                        <TableRow>
                            <TableCell>
                                <FormControlLabel control={<Checkbox checked={!recipients.results[i].disabled} onChange={(e, value, index, fieldName) => handleCheckBoxChange(e, value, i, "disabled")} />} />
                            </TableCell>
                            <TableCell>
                                <TextField name="name" id="name" value={recipients.results[i].name} onChange={(e, index, fieldName) => handleValueChange(e, i, "name")} label="Name" fullWidth />
                            </TableCell>
                            <TableCell>
                                <Autocomplete value={recipients.results[i].iso} onChange={(e, value, index, fieldName) => handleIsoValueChange(e, value, i, "iso")} options={(['', 'PJM', 'ISONE'])} getOptionLabel={(option) => option} renderInput={(params) => <TextField {...params} label="ISO" />} />
                            </TableCell>
                            <TableCell>
                                <Autocomplete value={recipients.results[i].zone} onChange={(e, value, index, fieldName) => handleZoneValueChange(e, value, i, "zone")} options={peakPeriods.filter(r => r.iso === 'PJM')} getOptionLabel={(option) => getOptionLabel(option)}
                                    renderInput={(params) => <TextField {...params} label="Zone" />} disabled={recipients.results[i].iso !== 'PJM'}
                                    getOptionSelected={(option, value) => getOptionSelected(option, value)} />
                            </TableCell>
                            <TableCell>
                                <FormControlLabel control={<Checkbox checked={recipients.results[i].emailEnabled} onChange={(e, value, index, fieldName) => handleCheckBoxChange(e, value, i, "emailEnabled")} />} />
                            </TableCell>
                            <TableCell>
                                <TextField name="emailAddress" id="emailAddress" value={recipients.results[i].emailAddress} onChange={(e, index, fieldName) => handleValueChange(e, i, "emailAddress")} label="Email Address" fullWidth disabled={!recipients.results[i].emailEnabled} />
                            </TableCell>
                            <TableCell>
                                <FormControlLabel control={<Checkbox checked={recipients.results[i].textEnabled} onChange={(e, value, index, fieldName) => handleCheckBoxChange(e, value, i, "textEnabled")} />} />
                            </TableCell>
                            <TableCell>
                                <NumberFormat name="phoneNumber" id="phoneNumber" value={recipients.results[i].phoneNumber} type="tel" format="(###) ###-####" mask="_" onValueChange={(value, index) => handlePhoneNumberValueChange(value, i)} label="Phone Number"
                                    disabled={!recipients.results[i].textEnabled} customInput={TextField} />
                            </TableCell>
                            <TableCell>
                                <div style={{ display: 'flex', flexDierction: 'row' }}>
                                    {renderAddIcon(recipients.results.length - 1, i)}
                                    <IconButton onClick={() => removeIconClick(i)}>
                                        <RemoveIcon />
                                    </IconButton>
                                </div>
                            </TableCell>
                        </TableRow>
                    </React.Fragment>);
            }
        }
        return rows;
    }

    return (
        <main className={classes.layout}>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={9} />
                <Grid item xs={6} sm={3} align="right">
                    <Button onClick={exportToCsv}>Export to CSV</Button>
                </Grid>
            </Grid>
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                    <TableHead sx={{ width: "max-content" }}>
                        <TableRow>
                            <TableCell width={50}>
                                <TableSortLabel active={orderBy === "disabled"} direction={orderBy === "disabled" ? direction : 'asc'}
                                    onClick={() => handleSort("disabled")}>
                                    Enabled
                                    {orderBy === "disabled" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={175}>
                                <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? direction : 'asc'}
                                    onClick={() => handleSort("name")}>
                                    Name
                                    {orderBy === "name" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={125}>
                                <TableSortLabel active={orderBy === "iso"} direction={orderBy === "iso" ? direction : 'asc'}
                                    onClick={() => handleSort("iso")}>
                                    ISO
                                    {orderBy === "iso" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={100}>
                                <TableSortLabel active={orderBy === "zone"} direction={orderBy === "zone" ? direction : 'asc'}
                                    onClick={() => handleSort("zone")}>
                                    Zone
                                    {orderBy === "zone" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={25}>
                                <TableSortLabel active={orderBy === "emailEnabled"} direction={orderBy === "emailEnabled" ? direction : 'asc'}
                                    onClick={() => handleSort("emailEnabled")}>
                                    EMail
                                    {orderBy === "emailEnabled" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel active={orderBy === "emailAddress"} direction={orderBy === "emailAddress" ? direction : 'asc'}
                                    onClick={() => handleSort("emailAddress")}>
                                    Email Address
                                    {orderBy === "emailAddress" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={25}>
                                <TableSortLabel active={orderBy === "textEnabled"} direction={orderBy === "textEnabled" ? direction : 'asc'}
                                    onClick={() => handleSort("textEnabled")}>
                                    Text
                                    {orderBy === "textEnabled" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={125}>
                                <TableSortLabel active={orderBy === "phoneNumber"} direction={orderBy === "phoneNumber" ? direction : 'asc'}
                                    onClick={() => handleSort("phoneNumber")}>
                                    Phone Number
                                    {orderBy === "phoneNumber" ? (<span className={classes.visuallyHidden}>{direction === "desc" ? 'sorted descending' : 'sorted ascending'}</span>) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={80} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderRecipients()}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination count={recipients.totalCount ? recipients.totalCount : 0} onPageChange={handlePageChange}
                                page={recipients.pageNumber ? recipients.pageNumber : 0}
                                rowsPerPage={calculateRowsPerPage(recipients.pageSize)} onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[10, 25, 50]} colSpan={9} ActionsComponent={PaginatedTableActions} />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={4} />
                <Grid item xs={6} sm={4} align="center">
                    <Button onClick={() => onSave()} variant="contained">Save</Button>
                </Grid>
                <Grid item xs={6} sm={4} />
            </Grid>
        </main >
    )

}

export default PeakManagementDistributionList;
